import { cn } from "@/utils/cn"
import { 
  Smartphone, 
  Server, 
  Mail, 
  Zap, 
  MapPin,
  AlertTriangle 
} from "lucide-react"

interface PatternIndicatorProps {
  patterns: {
    sequentialPhone?: boolean
    datacenterIP?: boolean
    suspiciousEmail?: boolean
    velocityAttack?: boolean
    zipConcentration?: boolean
  }
  layout?: 'horizontal' | 'vertical' | 'compact'
  showLabels?: boolean
  className?: string
}

export const PatternIndicator = ({ 
  patterns, 
  layout = 'horizontal',
  showLabels = true,
  className 
}: PatternIndicatorProps) => {
  const patternConfig = {
    sequentialPhone: { 
      icon: Smartphone, 
      label: 'Sequential Phone', 
      color: 'text-red-600 dark:text-red-400',
      bgColor: 'bg-red-50 dark:bg-red-900/20'
    },
    datacenterIP: { 
      icon: Server, 
      label: 'Data Center IP', 
      color: 'text-orange-600 dark:text-orange-400',
      bgColor: 'bg-orange-50 dark:bg-orange-900/20'
    },
    suspiciousEmail: { 
      icon: Mail, 
      label: 'Suspicious Email', 
      color: 'text-yellow-600 dark:text-yellow-400',
      bgColor: 'bg-yellow-50 dark:bg-yellow-900/20'
    },
    velocityAttack: { 
      icon: Zap, 
      label: 'Velocity Attack', 
      color: 'text-purple-600 dark:text-purple-400',
      bgColor: 'bg-purple-50 dark:bg-purple-900/20'
    },
    zipConcentration: { 
      icon: MapPin, 
      label: 'Zip Concentration', 
      color: 'text-blue-600 dark:text-blue-400',
      bgColor: 'bg-blue-50 dark:bg-blue-900/20'
    }
  }
  
  const activePatterns = Object.entries(patterns)
    .filter(([_, active]) => active)
    .map(([key]) => ({
      key,
      ...patternConfig[key as keyof typeof patternConfig]
    }))
  
  if (activePatterns.length === 0) {
    return (
      <div className={cn("text-muted-foreground text-sm", className)}>
        No patterns detected
      </div>
    )
  }
  
  const containerClass = cn(
    "flex items-center",
    layout === 'vertical' && 'flex-col items-start gap-2',
    layout === 'compact' && 'flex-wrap gap-1',
    layout === 'horizontal' && 'gap-2',
    className
  )
  
  return (
    <div className={containerClass} role="list" aria-label="Detected fraud patterns">
      {activePatterns.map((pattern, index) => {
        const IconComponent = pattern.icon
        
        return (
          <div 
            key={pattern.key}
            className={cn(
              "flex items-center gap-1.5 px-2 py-1 rounded-md transition-all duration-200",
              pattern.bgColor,
              layout === 'compact' && 'px-1.5 py-0.5'
            )}
            role="listitem"
            aria-label={`Pattern detected: ${pattern.label}`}
          >
            <IconComponent 
              className={cn(
                "h-4 w-4",
                layout === 'compact' && 'h-3 w-3',
                pattern.color
              )} 
            />
            {(showLabels && layout !== 'compact') && (
              <span className={cn(
                "text-xs font-medium",
                pattern.color
              )}>
                {pattern.label}
              </span>
            )}
          </div>
        )
      })}
      
      {activePatterns.length > 3 && layout === 'compact' && (
        <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-muted text-muted-foreground">
          <AlertTriangle className="h-3 w-3" />
          <span className="text-xs font-medium">
            +{activePatterns.length - 3} more
          </span>
        </div>
      )}
    </div>
  )
}